import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Save, RotateCcw, ExternalLink, Loader2, Languages } from "lucide-react";
import ImageUploader from "@/components/admin/ImageUploader";
import { TextField, TextAreaField, ArrayEditor, TagsEditor, ParagraphsEditor } from "@/components/admin/FormFields";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContentEditor({ password, onSaved }) {
  const [bundle, setBundle] = useState(null); // { zh, en }
  const [lang, setLang] = useState("zh");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/site-content`)
      .then((res) => {
        const data = res.data;
        if (data?.zh || data?.en) {
          setBundle({ zh: data.zh || {}, en: data.en || data.zh || {} });
        } else {
          setBundle({ zh: data, en: data });
        }
      })
      .catch(() => toast.error("加载失败 · Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  const content = bundle ? bundle[lang] : null;

  const update = (path) => (value) => {
    setBundle((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let obj = next[lang];
      for (let i = 0; i < path.length - 1; i++) obj = obj[path[i]];
      obj[path[path.length - 1]] = value;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/site-content`,
        { zh: bundle.zh, en: bundle.en },
        { headers: { "X-Admin-Password": password } }
      );
      toast.success("内容已保存 · Saved", { id: "save" });
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "保存失败 · Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("确定要恢复到默认内容吗？两种语言的修改都将丢失。")) return;
    setResetting(true);
    try {
      const res = await axios.post(
        `${API}/admin/site-content/reset`,
        {},
        { headers: { "X-Admin-Password": password } }
      );
      setBundle({ zh: res.data.zh, en: res.data.en });
      toast.success("已恢复默认内容 · Reset done");
      onSaved?.();
    } catch (err) {
      toast.error("重置失败");
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="py-20 text-center text-slate-400">
        <Loader2 size={24} className="mx-auto animate-spin" />
        <p className="mt-4 text-sm">加载中...</p>
      </div>
    );
  }

  if (!content) return <div className="py-20 text-center text-slate-400">加载失败</div>;

  return (
    <div>
      {/* Top action bar */}
      <div className="flex items-center justify-between bg-white border border-slate-200 rounded-sm p-4 mb-6 sticky top-16 z-20">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 bg-slate-100 rounded-sm p-1">
            <Languages size={14} className="ml-2 text-slate-500" />
            <button
              onClick={() => setLang("zh")}
              data-testid="editor-lang-zh"
              className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                lang === "zh" ? "bg-orange-600 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              中文
            </button>
            <button
              onClick={() => setLang("en")}
              data-testid="editor-lang-en"
              className={`px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                lang === "en" ? "bg-orange-600 text-white" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              English
            </button>
          </div>
          <div className="text-xs text-slate-500 hidden md:block">
            正在编辑 <span className="font-medium text-slate-900">{lang === "zh" ? "中文" : "English"}</span> 版本
          </div>
        </div>
        <div className="flex items-center gap-3">
          <a
            href={lang === "zh" ? "/" : "/?lang=en"}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs text-slate-600 hover:text-orange-600"
          >
            <ExternalLink size={14} /> 预览
          </a>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleReset}
            disabled={resetting}
            data-testid="content-reset-button"
            className="text-slate-600 hover:text-red-600"
          >
            <RotateCcw size={14} />
            <span className="ml-1 text-xs">恢复默认</span>
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            data-testid="content-save-button"
            className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-5"
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
            <span className="ml-2 text-xs">{saving ? "保存中..." : "保存两种语言"}</span>
          </Button>
        </div>
      </div>

      <Accordion type="multiple" defaultValue={["hero"]} className="space-y-4">
        {/* HERO */}
        <Section value="hero" title="Hero · 首屏区">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField label="徽章文字" value={content.hero.badge} onChange={update(["hero", "badge"])} />
            <TextField label="英文名" value={content.hero.name_en} onChange={update(["hero", "name_en"])} />
            <TextAreaField label="副标题（支持换行）" value={content.hero.subtitle} onChange={update(["hero", "subtitle"])} rows={2} />
            <TextAreaField label="描述段落" value={content.hero.description} onChange={update(["hero", "description"])} rows={3} />
            <TextField label="主按钮文字" value={content.hero.cta_primary} onChange={update(["hero", "cta_primary"])} />
            <TextField label="副按钮文字" value={content.hero.cta_secondary} onChange={update(["hero", "cta_secondary"])} />
            <TextField label="头像下方标签（如 Founder · CEO）" value={content.hero.founder_label} onChange={update(["hero", "founder_label"])} />
            <TextField label="头像下方姓名（中英文）" value={content.hero.founder_name} onChange={update(["hero", "founder_name"])} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6 pt-6 border-t border-slate-100">
            <ImageUploader
              label="Charles 人像照片"
              value={content.hero.portrait_image}
              onChange={update(["hero", "portrait_image"])}
              password={password}
              aspectClass="aspect-[4/5]"
            />
            <ImageUploader
              label="背景装饰图"
              value={content.hero.background_image}
              onChange={update(["hero", "background_image"])}
              password={password}
              aspectClass="aspect-video"
            />
          </div>
        </Section>

        {/* ACHIEVEMENTS */}
        <Section value="achievements" title="成就数据 · 4 个 Bento 卡片">
          <ArrayEditor
            label="成就项（按顺序对应大卡 / 小卡 / 小卡 / 宽卡）"
            items={content.achievements}
            onChange={update(["achievements"])}
            emptyItem={{ key: "new", value: "", suffix: "", label: "", detail: "" }}
            renderItem={(item, set) => (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <TextField label="Key（图标用：sales / years / leader / mdrt）" value={item.key} onChange={(v) => set({ ...item, key: v })} />
                <TextField label="大数字（如 RM 15M+）" value={item.value} onChange={(v) => set({ ...item, value: v })} />
                <TextField label="中文标签（如 团体业绩）" value={item.suffix} onChange={(v) => set({ ...item, suffix: v })} />
                <TextField label="英文标签（如 Group Sales）" value={item.label} onChange={(v) => set({ ...item, label: v })} />
                <div className="md:col-span-2">
                  <TextAreaField label="详细说明" value={item.detail} onChange={(v) => set({ ...item, detail: v })} rows={2} />
                </div>
              </div>
            )}
          />
        </Section>

        {/* ABOUT CHARLES */}
        <Section value="about" title="关于我 · 个人故事">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField label="标题上半部分" value={content.about_charles.heading_top} onChange={update(["about_charles", "heading_top"])} />
            <TextField label="标题下半部分（橙色高亮）" value={content.about_charles.heading_bottom} onChange={update(["about_charles", "heading_bottom"])} />
          </div>
          <div className="mt-5">
            <ParagraphsEditor label="自我介绍段落" paragraphs={content.about_charles.paragraphs} onChange={update(["about_charles", "paragraphs"])} />
          </div>
          <div className="mt-5">
            <TagsEditor label="标签（专业能力）" tags={content.about_charles.tags} onChange={update(["about_charles", "tags"])} />
          </div>
          <div className="mt-5">
            <ArrayEditor
              label="人生时间线"
              items={content.about_charles.timeline}
              onChange={update(["about_charles", "timeline"])}
              emptyItem={{ year: "", title: "", text: "" }}
              renderItem={(item, set) => (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <TextField label="年份" value={item.year} onChange={(v) => set({ ...item, year: v })} />
                  <TextField label="标题" value={item.title} onChange={(v) => set({ ...item, title: v })} />
                  <TextField label="描述" value={item.text} onChange={(v) => set({ ...item, text: v })} />
                </div>
              )}
            />
          </div>
        </Section>

        {/* ISI GROUP */}
        <Section value="isi" title="iSi Group · 公司介绍">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField label="标题上半部分" value={content.isi_group.heading_top} onChange={update(["isi_group", "heading_top"])} />
            <TextField label="标题下半部分（橙色高亮）" value={content.isi_group.heading_bottom} onChange={update(["isi_group", "heading_bottom"])} />
            <div className="md:col-span-2">
              <TextAreaField label="描述" value={content.isi_group.description} onChange={update(["isi_group", "description"])} rows={3} />
            </div>
          </div>
          <div className="mt-6">
            <ImageUploader
              label="背景图（KL 天际线/城市图）"
              value={content.isi_group.background_image}
              onChange={update(["isi_group", "background_image"])}
              password={password}
              aspectClass="aspect-video"
            />
          </div>
          <div className="mt-6">
            <ArrayEditor
              label="3 大支柱"
              items={content.isi_group.pillars}
              onChange={update(["isi_group", "pillars"])}
              emptyItem={{ num: "", title: "", text: "" }}
              renderItem={(item, set) => (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <TextField label="编号（01 / 02 / 03）" value={item.num} onChange={(v) => set({ ...item, num: v })} />
                  <TextField label="标题" value={item.title} onChange={(v) => set({ ...item, title: v })} />
                  <TextField label="描述" value={item.text} onChange={(v) => set({ ...item, text: v })} />
                </div>
              )}
            />
          </div>
        </Section>

        {/* SERVICES */}
        <Section value="services" title="服务 · 6 个服务卡片">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField label="标题上半部分" value={content.services.heading_top} onChange={update(["services", "heading_top"])} />
            <TextField label="标题下半部分" value={content.services.heading_bottom} onChange={update(["services", "heading_bottom"])} />
            <div className="md:col-span-2">
              <TextAreaField label="描述" value={content.services.description} onChange={update(["services", "description"])} rows={2} />
            </div>
          </div>
          <div className="mt-6">
            <ArrayEditor
              label="服务项"
              items={content.services.items}
              onChange={update(["services", "items"])}
              emptyItem={{ title: "", en: "", text: "" }}
              renderItem={(item, set) => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <TextField label="中文标题" value={item.title} onChange={(v) => set({ ...item, title: v })} />
                  <TextField label="英文标签" value={item.en} onChange={(v) => set({ ...item, en: v })} />
                  <div className="md:col-span-2">
                    <TextAreaField label="服务描述" value={item.text} onChange={(v) => set({ ...item, text: v })} rows={2} />
                  </div>
                </div>
              )}
            />
          </div>
        </Section>

        {/* TESTIMONIALS */}
        <Section value="testimonials" title="客户见证">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField label="标题上半部分" value={content.testimonials.heading_top} onChange={update(["testimonials", "heading_top"])} />
            <TextField label="标题下半部分" value={content.testimonials.heading_bottom} onChange={update(["testimonials", "heading_bottom"])} />
          </div>
          <div className="mt-6">
            <ArrayEditor
              label="客户见证"
              items={content.testimonials.items}
              onChange={update(["testimonials", "items"])}
              emptyItem={{ quote: "", name: "", role: "", avatar: "" }}
              renderItem={(item, set) => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    <TextAreaField label="引言" value={item.quote} onChange={(v) => set({ ...item, quote: v })} rows={3} />
                  </div>
                  <TextField label="姓名" value={item.name} onChange={(v) => set({ ...item, name: v })} />
                  <TextField label="头衔" value={item.role} onChange={(v) => set({ ...item, role: v })} />
                  <div className="md:col-span-2">
                    <ImageUploader label="头像" value={item.avatar} onChange={(v) => set({ ...item, avatar: v })} password={password} aspectClass="aspect-square" />
                  </div>
                </div>
              )}
            />
          </div>
        </Section>

        {/* CONTACT & WHATSAPP */}
        <Section value="contact" title="联系方式 / WhatsApp">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField label="标题上半部分" value={content.contact.heading_top} onChange={update(["contact", "heading_top"])} />
            <TextField label="标题下半部分" value={content.contact.heading_bottom} onChange={update(["contact", "heading_bottom"])} />
            <div className="md:col-span-2">
              <TextAreaField label="描述" value={content.contact.description} onChange={update(["contact", "description"])} rows={2} />
            </div>
            <TextField label="邮箱" value={content.contact.email} onChange={update(["contact", "email"])} />
            <TextField label="电话（显示用）" value={content.contact.phone} onChange={update(["contact", "phone"])} />
            <TextField label="地址" value={content.contact.address} onChange={update(["contact", "address"])} />
            <TextField label="WhatsApp 号码（纯数字，不带 + 号，如 60143148627）" value={content.contact.whatsapp} onChange={update(["contact", "whatsapp"])} />
            <div className="md:col-span-2">
              <TextAreaField label="WhatsApp 预设消息" value={content.contact.whatsapp_message} onChange={update(["contact", "whatsapp_message"])} rows={2} />
            </div>
          </div>
        </Section>

        {/* FOOTER */}
        <Section value="footer" title="页脚 · 社交媒体 · 分析追踪">
          <div className="grid grid-cols-1 gap-5">
            <TextAreaField label="页脚标语" value={content.footer.tagline} onChange={update(["footer", "tagline"])} rows={3} />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <TextField label="Instagram 链接" value={content.footer.instagram} onChange={update(["footer", "instagram"])} placeholder="https://instagram.com/..." />
              <TextField label="LinkedIn 链接" value={content.footer.linkedin} onChange={update(["footer", "linkedin"])} placeholder="https://linkedin.com/in/..." />
              <TextField label="Facebook 链接" value={content.footer.facebook} onChange={update(["footer", "facebook"])} placeholder="https://facebook.com/..." />
            </div>
            <TextField
              label="Google Analytics 4 · Measurement ID（可选）"
              value={content.footer.ga4_measurement_id}
              onChange={update(["footer", "ga4_measurement_id"])}
              placeholder="G-XXXXXXXXXX"
            />
            <p className="text-xs text-slate-500 -mt-2">
              在 <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-orange-600 underline">analytics.google.com</a> 建立 GA4 属性，复制 Measurement ID（以 G- 开头）粘贴到此处；留空则不启用。
            </p>
          </div>
        </Section>

        {/* NAV & SECTION LABELS */}
        <Section value="nav" title="导航 · 版块标签 · UI 文字">
          <div className="text-xs text-slate-500 mb-4">导航与版块的小标签（如 "关于我 · About Charles"）</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField label="导航 · 关于" value={content.nav?.about} onChange={update(["nav", "about"])} />
            <TextField label="导航 · iSi Group" value={content.nav?.isigroup} onChange={update(["nav", "isigroup"])} />
            <TextField label="导航 · 服务" value={content.nav?.services} onChange={update(["nav", "services"])} />
            <TextField label="导航 · 见证" value={content.nav?.testimonials} onChange={update(["nav", "testimonials"])} />
            <TextField label="导航 · 联系" value={content.nav?.contact} onChange={update(["nav", "contact"])} />
            <TextField label="导航 · CTA 按钮文字" value={content.nav?.cta} onChange={update(["nav", "cta"])} />
          </div>
          <div className="mt-6 pt-6 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField label="成就区小标签" value={content.sections?.achievements_label} onChange={update(["sections", "achievements_label"])} />
            <TextField label="成就区标题上" value={content.sections?.achievements_title_top} onChange={update(["sections", "achievements_title_top"])} />
            <TextField label="成就区标题下" value={content.sections?.achievements_title_bottom} onChange={update(["sections", "achievements_title_bottom"])} />
            <div className="md:col-span-2">
              <TextAreaField label="成就区描述" value={content.sections?.achievements_desc} onChange={update(["sections", "achievements_desc"])} rows={2} />
            </div>
            <TextField label="关于我区小标签" value={content.sections?.about_label} onChange={update(["sections", "about_label"])} />
            <TextField label="iSi Group 区小标签" value={content.sections?.isigroup_label} onChange={update(["sections", "isigroup_label"])} />
            <TextField label="服务区小标签" value={content.sections?.services_label} onChange={update(["sections", "services_label"])} />
            <TextField label="见证区小标签" value={content.sections?.testimonials_label} onChange={update(["sections", "testimonials_label"])} />
            <TextField label="联系区小标签" value={content.sections?.contact_label} onChange={update(["sections", "contact_label"])} />
            <TextField label="Hero 向下滚动提示" value={content.sections?.scroll_hint} onChange={update(["sections", "scroll_hint"])} />
            <TextField label="WhatsApp 按钮文字" value={content.sections?.whatsapp_cta} onChange={update(["sections", "whatsapp_cta"])} />
          </div>
        </Section>
      </Accordion>

      {/* Bottom action */}
      <div className="mt-8 flex justify-end gap-3">
        <Button
          onClick={handleSave}
          disabled={saving}
          data-testid="content-save-button-bottom"
          className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 h-12"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          <span className="ml-2">{saving ? "保存中..." : "保存所有修改"}</span>
        </Button>
      </div>
    </div>
  );
}

function Section({ value, title, children }) {
  return (
    <AccordionItem value={value} className="bg-white border border-slate-200 rounded-sm px-6 data-[state=open]:shadow-sm">
      <AccordionTrigger className="py-5 hover:no-underline">
        <span className="font-serif-sc text-lg text-slate-900">{title}</span>
      </AccordionTrigger>
      <AccordionContent className="pt-2 pb-6">{children}</AccordionContent>
    </AccordionItem>
  );
}
